import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import {
  formValues,
  installmentFormValues,
  InstallmentSchema,
  schema,
} from "./validations";
import { useNavigate, useParams } from "react-router-dom";
import { getSchoolRedirectPath } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { TabsDataTypes } from "../types";
import {
  AGREEMENT_DOCUMENT,
  AGREEMENT_DOCUMENT_FILE_NAME,
  CONTRACT_YEARS,
  DEAL_AMOUNT,
  DEAL_CLOSED_BY,
  DEAL_CLOSURE_DATE,
  EXPECTED_DUE_DATE,
  EXPECTED_LAUNCH_DATE,
  INCLUDES_GST,
  INSTALLMENT,
  INSTALLMENT_AMOUNT,
  INSTALLMENT_NUMBER,
  INSTALLMENT_YEAR,
  INTEREST_PERCENTAGE,
  LEAD_TYPE,
  MOU_AMOUNT,
  PAYMENT_FROM_ENTITY,
  PDCS_COLLECTED,
  SALES_PERSON_TYPE,
} from "./consts";
import {
  useGetInstallmentsByCommercialIdQuery,
  useGetSchoolCommercialBySchoolIdQuery,
  useCreateSchoolCommercialMutation,
  useCreateSchoolInstallmentsMutation,
  useUpdateSchoolCommercialMutation,
} from "@/services";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useAppSelector } from "@/redux/hooks";
import { useProfilesByRoleQuery } from "@/services/employeeManagement/useProfilesByRoleQuery";
import { LOGIN_ROLES } from "@/lib/utils";

const useInstallmentSchedule = ({ onNextClick }: TabsDataTypes) => {
  const navigate = useNavigate();
  const { id, action } = useParams();
  const attachmentRef = useRef<any>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [openUpdatePdc, setOpenUpdatePdc] = useState<boolean>(false);
  const [openAddInstallment, setOpenAddInstallment] = useState<boolean>(false);
  const [selectedInstallmentIndex, setSelectedInstallmentIndex] = useState<
    number | null
  >(null);
  const isEdit = action === "edit" && !!id;

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      // Deal Info
      [DEAL_CLOSURE_DATE]: "",
      [DEAL_AMOUNT]: "",
      [INCLUDES_GST]: "",
      [PDCS_COLLECTED]: "Yes",
      [SALES_PERSON_TYPE]: "",
      [DEAL_CLOSED_BY]: "",
      [LEAD_TYPE]: "",
      [CONTRACT_YEARS]: "",
      [PAYMENT_FROM_ENTITY]: "",
      [EXPECTED_LAUNCH_DATE]: "",
      [AGREEMENT_DOCUMENT]: null,

      // Installment Schedule
      [INSTALLMENT]: Array.from({ length: 1 }, () => ({
        [INSTALLMENT_YEAR]: "",
        [INSTALLMENT_NUMBER]: "1",
        [EXPECTED_DUE_DATE]: "",
        [INSTALLMENT_AMOUNT]: "0",
        [MOU_AMOUNT]: "0",
        [INTEREST_PERCENTAGE]: "0",
        pdcData: [] as any[],
      })),
    },
  });

  const InstallmentForm = useForm<installmentFormValues>({
    resolver: zodResolver(InstallmentSchema),
    defaultValues: {
      [INSTALLMENT_YEAR]: "",
      [INSTALLMENT_NUMBER]: "0",
      [EXPECTED_DUE_DATE]: "",
      [INSTALLMENT_AMOUNT]: "0",
      [MOU_AMOUNT]: "0",
      [INTEREST_PERCENTAGE]: "0",
    },
  });

  const requiredFields = getRequiredFields(schema);
  // Get school ID from Redux store
  const schoolOnboardingId = useAppSelector(
    (state: RootState) => state.currentData.schoolOnboardingId
  );

  const toYMD = (value: any) => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
  };

  // 🔹 Automatically calculate Installment Amount as user types
  useEffect(() => {
    // get the current installments data
    const installments = form.getValues(INSTALLMENT);

    if (!installments || !Array.isArray(installments)) return;

    installments.forEach((installment, index) => {
      const mouAmount = parseFloat(installment?.[MOU_AMOUNT] ?? "");
      const interestPercent = parseFloat(
        installment?.[INTEREST_PERCENTAGE] ?? ""
      );

      if (!isNaN(mouAmount) && !isNaN(interestPercent)) {
        const interestAmount = (mouAmount * interestPercent) / 100;
        form.setValue(
          `${INSTALLMENT}.${index}.${INSTALLMENT_AMOUNT}`,
          interestAmount.toFixed(2)
        );
      } else {
        form.setValue(`${INSTALLMENT}.${index}.${INSTALLMENT_AMOUNT}`, "");
      }
    });
  }, [form.getValues(INSTALLMENT)]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (typeof name !== "string") return;

      if (!name.includes(MOU_AMOUNT) && !name.includes(INTEREST_PERCENTAGE))
        return;

      const match = name.match(/installment\.(\d+)\./);
      if (!match) return;
      const index = Number(match[1]);

      const mouAmount = parseFloat(
        value?.installment?.[index]?.mouAmount ?? ""
      );
      const interestPercent = parseFloat(
        value?.installment?.[index]?.interestPercentage ?? ""
      );

      if (!isNaN(mouAmount) && !isNaN(interestPercent)) {
        const interestAmount = (mouAmount * interestPercent) / 100;
        form.setValue(
          `installment.${index}.installmentAmount`,
          interestAmount.toFixed(2),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
      } else {
        form.setValue(`installment.${index}.installmentAmount`, "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // Edit mode: fetch commercial and installments, prefill form
  const { data: commercialResp, isPending } =
    useGetSchoolCommercialBySchoolIdQuery(
      String(id) || schoolOnboardingId || "",
      { enabled: isEdit } as any
    );
  const commercial = useMemo(() => {
    const commercialData = (commercialResp as any)?.data?.[0]?.records;

    // If data is an array, pick the first commercial
    if (Array.isArray(commercialData) && commercialData.length > 0) {
      return commercialData[0];
    }
    // If data is an object, use it directly
    return commercialData || {};
  }, [commercialResp]);

  const commercialId =
    commercial?.commercialId || commercial?.id || commercial?._id || "";

  const { data: installmentsResp, isPending: isDataLoading } =
    useGetInstallmentsByCommercialIdQuery(commercialId, {
      enabled: isEdit && !!commercialId,
    } as any);

  // Fetch salesops employees for Deal Closed By dropdown
  const { data: salesOpsEmployeesData } = useProfilesByRoleQuery(
    LOGIN_ROLES.SALES_OPS
  );

  const dealClosedByOptions =
    salesOpsEmployeesData?.data?.[0]?.records?.map((profile: any) => ({
      label: profile.name,
      value: profile.profile_id,
    })) || [];

  useEffect(() => {
    if (!isEdit) return;

    const dealDefaults: Partial<formValues> = {
      [DEAL_CLOSURE_DATE]: commercial?.dealCloserDate ?? "",
      [DEAL_AMOUNT]: String(commercial?.dealAmount ?? ""),
      [INCLUDES_GST]: commercial?.includesGst ?? "",
      [PDCS_COLLECTED]: commercial?.isPdcCollected ? "Yes" : "No",
      [SALES_PERSON_TYPE]: commercial?.salesPersonType ?? "",
      [DEAL_CLOSED_BY]: commercial?.dealClosedBy ?? "",
      [LEAD_TYPE]: commercial?.leadType ?? "",
      [CONTRACT_YEARS]: String(commercial?.contractYear) ?? "",
      [PAYMENT_FROM_ENTITY]: commercial?.paymentFromEntity ?? "",
      [EXPECTED_LAUNCH_DATE]: commercial?.expectedLaunchDate ?? "",
      [AGREEMENT_DOCUMENT]: commercial?.aggrementDocument || null,
            [AGREEMENT_DOCUMENT_FILE_NAME]:
              commercial ? commercial?.agreementDocumentFileName : "",
    } as any;

    const installmentsData = (installmentsResp as any)?.data;
    // Handle both array and object responses
    const rawInstallments = Array.isArray(installmentsData)
      ? installmentsData
      : installmentsData
      ? [installmentsData]
      : [];

    const mappedInstallments = rawInstallments.length
      ? rawInstallments.map((it) => ({
          _id: it?.installmentId || "",
          [INSTALLMENT_YEAR]: String(it?.year) ?? "",
          [INSTALLMENT_NUMBER]: String(it?.installmentNumber ?? "0"),
          [EXPECTED_DUE_DATE]: it?.expectedDueDate ?? "",
          [INSTALLMENT_AMOUNT]: String(it?.installmentAmount ?? "0"),
          [MOU_AMOUNT]: String(it?.mouAmount ?? "0"),
          [INTEREST_PERCENTAGE]: String(it?.installmentPercentage ?? "0"),
          pdcData: it?.pdcData || [],
        }))
      : undefined;

    form.reset({
      ...(form.getValues() as any),
      ...(dealDefaults as any),
      ...(mappedInstallments
        ? { [INSTALLMENT]: mappedInstallments as any }
        : {}),
    });
  }, [isEdit, commercialResp, installmentsResp]);

  const { mutateAsync: createCommercial } = useCreateSchoolCommercialMutation();
  const { mutateAsync: createInstallments } =
    useCreateSchoolInstallmentsMutation({
      onSuccess: () => {
        InstallmentForm.reset();
        onCancelInstallment();
      },
    });
  const { mutateAsync: updateCommercial } = useUpdateSchoolCommercialMutation();

  const onSubmit = async (data: formValues) => {
    const cleaned = setEmptyFieldsToNull(removeEmptyFields(data)) as any;
    // Prepare Commercial payload (create or update)
    const commercialPayload = {
      schoolId: schoolOnboardingId || String(id) || "",
      dealCloserDate: toYMD(cleaned[DEAL_CLOSURE_DATE]),
      dealAmount: Number(cleaned[DEAL_AMOUNT] || 0),
      isGstIncluded: cleaned[INCLUDES_GST] || "",
      isPdcCollected: cleaned[PDCS_COLLECTED] ?? "No",
      salesPersonType: cleaned[SALES_PERSON_TYPE] || "",
      dealClosedBy: cleaned[DEAL_CLOSED_BY] || "",
      leadType: cleaned[LEAD_TYPE] || "",
      contractYear: cleaned[CONTRACT_YEARS] || "",
      paymentFromEntity: cleaned[PAYMENT_FROM_ENTITY] || "",
      expectedLaunchDate: toYMD(cleaned[EXPECTED_LAUNCH_DATE]),
      aggrementDocument: cleaned[AGREEMENT_DOCUMENT] || null,
    };

    // 1) Create or Update Commercial
    let commercialRes: any;
    if (isEdit && commercialId) {
      commercialRes = await updateCommercial({
        commercialId,
        ...commercialPayload,
      });
    } else {
      commercialRes = await createCommercial(commercialPayload);
    }
    const savedCommercial = commercialRes?.data || {};
    const savedCommercialId =
      savedCommercial?.commercialId ||
      savedCommercial?.id ||
      savedCommercial?._id ||
      commercialId;

    // 2) Create Installments
    const installmentsData = (cleaned[INSTALLMENT] || []).map((it: any) => {
      const isPdcAvailable =
        String(cleaned[PDCS_COLLECTED] || "no").toLowerCase() === "yes";
      const payload: any = {
        schoolId: schoolOnboardingId || String(id) || "",
        commercialId: savedCommercialId,
        year: Number(it[INSTALLMENT_YEAR] || 0),
        installmentNumber: Number(it[INSTALLMENT_NUMBER] || 0),
        expectedDueDate: toYMD(it[EXPECTED_DUE_DATE]),
        installmentAmount: Number(it[INSTALLMENT_AMOUNT] || 0),
        mouAmount: Number(it[MOU_AMOUNT] || 0),
        installmentPercentage: Number(it[INTEREST_PERCENTAGE] || 0),
        isPdcCheckAvailable: isPdcAvailable,
      };

      // Include PDC data if available and PDCs are collected
      if (
        isPdcAvailable &&
        it.pdcData &&
        Array.isArray(it.pdcData) &&
        it.pdcData.length > 0
      ) {
        payload.pdcData = it.pdcData.map((pdc: any) => ({
          year: Number(pdc.year || 0),
          pdcNumber: Number(pdc.pdcNumber || 0),
          pdcDate: toYMD(pdc.pdcDate),
          pdcAmount: Number(pdc.pdcAmount || 0),
          pdcRemarks: pdc.pdcRemarks || null,
        }));
      }

      return payload;
    });

    // 2) Create Installments
    if (!isEdit) {
      await createInstallments({ installmentsData });
    } else {
      // No update API: only create installments that don't already exist
      const existingInstallmentsData = (installmentsResp as any)?.data;
      const existingInstallments = Array.isArray(existingInstallmentsData)
        ? existingInstallmentsData
        : existingInstallmentsData
        ? [existingInstallmentsData]
        : [];

      const existingList = existingInstallments.map((it) => ({
        year: Number(it?.year ?? 0),
        installmentNumber: Number(it?.installmentNumber ?? 0),
      }));
      const existingKey = new Set(
        existingList.map((it) => `${it.year}__${it.installmentNumber}`)
      );

      const newOnes = installmentsData.filter((it: any) => {
        const key = `${Number(it.year)}__${Number(it.installmentNumber)}`;
        return !existingKey.has(key);
      });

      if (newOnes.length > 0) {
        await createInstallments({ installmentsData: newOnes });
      }
    }

    onNextClick();
  };

  const onSubmitNewInstallment = async (data: installmentFormValues) => {
    const cleaned = setEmptyFieldsToNull(removeEmptyFields(data)) as any;

    const installmentsData = {
      schoolId: schoolOnboardingId || String(id) || "",
      commercialId: commercialId,
      year: Number(data[INSTALLMENT_YEAR] || 0),
      installmentNumber: Number(data[INSTALLMENT_NUMBER] || 0),
      expectedDueDate: toYMD(data[EXPECTED_DUE_DATE]),
      installmentAmount: Number(data[INSTALLMENT_AMOUNT] || 0),
      mouAmount: Number(data[MOU_AMOUNT] || 0),
      installmentPercentage: Number(data[INTEREST_PERCENTAGE] || 0),
      isPdcCheckAvailable:
        String(cleaned[PDCS_COLLECTED] || "no").toLowerCase() === "yes",
    };

    await createInstallments({ installmentsData: [installmentsData] });
  };

  const onCancel = () => {
    const userRole = loggedInUserData?.role || [];
    navigate(getSchoolRedirectPath(userRole));
  };

  const handleScrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth", // smooth scroll
      });
    }
  };

  const onAddNewAcademicInfo = () => {
    if (!isEdit) {
      form.setValue(INSTALLMENT, [
        ...form.getValues(INSTALLMENT),
        {
          [INSTALLMENT_YEAR]: "",
          [INSTALLMENT_NUMBER]: "0",
          [EXPECTED_DUE_DATE]: "",
          [INSTALLMENT_AMOUNT]: "0",
          [MOU_AMOUNT]: "0",
          [INTEREST_PERCENTAGE]: "0",
          pdcData: [],
        },
      ]);
      setTimeout(() => handleScrollToBottom(), 200);
    } else {
      setOpenAddInstallment(true);
    }
  };

  const onRemoveAcademicInfo = (index: number) => {
    const tasks = [...form.getValues(INSTALLMENT)];
    tasks.splice(index, 1);
    form.setValue(INSTALLMENT, tasks);
    setTimeout(() => handleScrollToBottom(), 200);
  };

  const onCancelUpdatePdc = () => {
    setOpenUpdatePdc(false);
    setSelectedInstallmentIndex(null);
  };

  const onUpdatePdcClick = (index: number) => {
    setSelectedInstallmentIndex(index);
    setOpenUpdatePdc(true);
  };

  const onCancelInstallment = () => {
    setOpenAddInstallment(false);
  };

  return {
    form,
    InstallmentForm,
    requiredFields,
    onSubmit,
    onCancel,
    attachmentRef,
    onAddNewAcademicInfo,
    onRemoveAcademicInfo,
    scrollRef,
    openUpdatePdc,
    onCancelUpdatePdc,
    onUpdatePdcClick,
    openAddInstallment,
    onCancelInstallment,
    onSubmitNewInstallment,
    isLoading: isPending || isDataLoading,
    selectedInstallmentIndex,
    dealClosedByOptions,
  };
};

export default useInstallmentSchedule;
